import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export interface MenuItem {
  key: string
  label: string
  hint?: string | undefined
  onSelect: () => void
  /** Renders a checkbox-style item (menuitemcheckbox). */
  checked?: boolean | undefined
}

export interface MenuSection {
  key: string
  heading?: string | undefined
  items: MenuItem[]
}

interface Props {
  /** Accessible name of the menu. */
  label: string
  /** Trigger contents. */
  trigger: ReactNode
  triggerClassName: string
  triggerTitle?: string
  sections: MenuSection[]
  /** Where the menu opens relative to the trigger. */
  placement?: 'below-end' | 'above-start'
}

/**
 * Small popover menu for the shell (+ New and the profile menu).
 * position: fixed from the trigger's rect so the scrolling rail can't clip it.
 * Escape and outside clicks close it; arrow keys move between items.
 */
export function Menu({
  label,
  trigger,
  triggerClassName,
  triggerTitle,
  sections,
  placement = 'below-end',
}: Props) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left?: number; right?: number }>(
    {},
  )
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  useEffect(() => {
    if (!open) return
    menuRef.current?.querySelector<HTMLButtonElement>('[role^="menuitem"]')?.focus()
  }, [open])

  function toggle() {
    if (!open && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect()
      const vw = typeof window !== 'undefined' ? window.innerWidth : 0
      const vh = typeof window !== 'undefined' ? window.innerHeight : 0
      if (placement === 'above-start') setPos({ bottom: vh - r.top + 6, left: Math.max(8, r.left) })
      else setPos({ top: r.bottom + 6, right: Math.max(8, vw - r.right) })
    }
    setOpen((v) => !v)
  }

  function onMenuKey(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const items = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]') ?? [],
    )
    const i = items.indexOf(document.activeElement as HTMLButtonElement)
    const next =
      e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length
    items[next]?.focus()
  }

  return (
    <div className="shell-menu-root" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={triggerTitle}
        onClick={toggle}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={menuRef}
          className="shell-menu"
          role="menu"
          aria-label={label}
          style={pos}
          onKeyDown={onMenuKey}
        >
          {sections.map((section) => (
            <div
              key={section.key}
              className="shell-menu-section"
              role="group"
              aria-label={section.heading}
            >
              {section.heading && <div className="shell-menu-heading">{section.heading}</div>}
              {section.items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
                  aria-checked={item.checked === undefined ? undefined : item.checked}
                  className="shell-menu-item"
                  onClick={() => {
                    close(false)
                    item.onSelect()
                  }}
                >
                  <span className="shell-menu-label">{item.label}</span>
                  {item.checked !== undefined && (
                    <span
                      className={`shell-switch${item.checked ? ' on' : ''}`}
                      aria-hidden="true"
                    />
                  )}
                  {item.hint && <span className="shell-menu-hint">{item.hint}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
